// src/pages/outsourcing_management/requisitions/RequisitionDetailItemsTable.tsx
//
// EE-207 Phase 4 (#13) - read-only item-lines table for the detail view.
// Unlike the create/edit form's client-side material-total preview
// (requisitionForm.calculateMaterialPreview), everything rendered
// here - total_cutting_length_mm / total_required_material_mm per line, plus
// the aggregate footer - comes straight from the server response
// (RequisitionItemVM, persisted by
// RequisitionController.calculate_material at save time), matching
// the "server-computed totals" success criterion.
import { Fragment, FunctionComponent } from "react";
import AppLink from "../AppLink";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { RequisitionItemType } from "types/RequisitionTypes";
import {
  isMaterialType,
  isPartBasedType,
  REQ_TYPE_TOOL,
} from "./requisitionForm";
import { formatAmount } from "util/TextUtil";

type PropsType = {
  typeCode: string | null;
  items: RequisitionItemType[];
  // Set when lines can be cancelled one by one (a submitted requisition with
  // more than one open line).
  onCancelLine?: (item: RequisitionItemType) => void;
};

const isCancelled = (item: RequisitionItemType) => item.line_state === "CANCELLED";


const fmt = (value: number | null | undefined) =>
  value === null || value === undefined ? "-" : value.toLocaleString();

// The item lines must not scroll sideways inside the detail dialog: TableContainer
// defaults to `overflow-x: auto`, which turned this table into a horizontally
// scrollable strip. Fixed layout + percentage column widths + wrapping cells make
// every column fit the dialog width instead.
const NO_SCROLL_CONTAINER_SX = { overflowX: "visible" as const };
const FIT_TABLE_SX = {
  tableLayout: "fixed" as const,
  width: "100%",
  "& .MuiTableCell-root": {
    whiteSpace: "normal" as const,
    overflowWrap: "anywhere" as const,
    px: 1,
  },
};

const dimensionsOf = (item: RequisitionItemType) =>
  item.diameter_mm != null
    ? `Ø${fmt(item.diameter_mm)} mm`
    : item.width_mm != null || item.height_mm != null
    ? `${fmt(item.width_mm)} × ${fmt(item.height_mm)} mm`
    : null;

const NUMBERS_SX = { whiteSpace: "nowrap !important", fontVariantNumeric: "tabular-nums" };

// A line with a note continues in the row below, so no rule between them.
const NOTED_ROW_SX = { "& > .MuiTableCell-root": { borderBottom: "none" } };

// A cancelled line stays listed, greyed and struck through - all but its
// state, which says Cancelled.
const rowSx = (item: RequisitionItemType, noted = false) => ({
  "& > .MuiTableCell-root": {
    ...(noted ? NOTED_ROW_SX["& > .MuiTableCell-root"] : {}),
    ...(isCancelled(item) ? { color: "text.disabled" } : {}),
  },
  ...(isCancelled(item)
    ? { "& > .MuiTableCell-root:not([data-line-state])": { textDecoration: "line-through" } }
    : {}),
});

// EE-253 P2b: where each line is in purchasing - its Inquiry,
// and in a column of its own the line's state and how much of its share is
// ready to use. Plain text, two lines on every row, so the rows read alike
// and line up like the Progress column on the Requisition list.
const LineHeadCells: FunctionComponent = () => (
  <>
    <TableCell sx={{ width: "11%" }}>Inquiry</TableCell>
    <TableCell sx={{ width: "14%" }}>Progress</TableCell>
  </>
);

const LineCells: FunctionComponent<{
  item: RequisitionItemType;
  onCancelLine?: (item: RequisitionItemType) => void;
}> = ({ item, onCancelLine }) => (
  <>
    <TableCell sx={{ whiteSpace: "nowrap !important" }}>
      {item.inquiry_id ? (
        <AppLink to={`/inquiries/${item.inquiry_id}`}>{`INQ-${String(item.inquiry_id).padStart(6, "0")}`}</AppLink>
      ) : (
        "-"
      )}
    </TableCell>
    <TableCell
      data-testid={`req-line-state-${item.line_no ?? item.id}`}
      data-line-state
      sx={{ fontSize: "0.75rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap !important" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <span>{item.line_state_label ?? "-"}</span>
        {onCancelLine && !isCancelled(item) ? (
          <Tooltip title="Cancel this line">
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              onClick={() => onCancelLine(item)}
              data-testid={`req-line-cancel-${item.line_no ?? item.id}`}
            >
              <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      {item.ready_need ? (
        <div>
          {formatAmount(item.ready_got ?? 0)}/{formatAmount(item.ready_need)}
          {item.ready_unit === "mm" ? " mm" : ""} ready
        </div>
      ) : null}
    </TableCell>
  </>
);

// EE-253 P2b: the type is on the line, so one requisition can
// hold several types - one table per type, in the order the lines were made.
const RequisitionDetailItemsTable: FunctionComponent<PropsType> = ({ typeCode, items, onCancelLine }) => {
  const groups: { code: string | null; label: string; items: RequisitionItemType[] }[] = [];
  items.forEach((item) => {
    const code = item.type_code ?? typeCode;
    let group = groups.find((g) => g.code === code);
    if (!group) {
      group = { code, label: item.type_label ?? code ?? "", items: [] };
      groups.push(group);
    }
    group.items.push(item);
  });
  const showLine = items.some((item) => item.line_state);
  if (groups.length <= 1)
    return (
      <TypeItemsTable
        typeCode={groups[0]?.code ?? typeCode}
        items={items}
        showLine={showLine}
        onCancelLine={onCancelLine}
      />
    );
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {groups.map((group) => (
        <Box key={group.code ?? ""}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {group.label}
          </Typography>
          <TypeItemsTable
            typeCode={group.code}
            items={group.items}
            showLine={showLine}
            onCancelLine={onCancelLine}
          />
        </Box>
      ))}
    </Box>
  );
};

const TypeItemsTable: FunctionComponent<PropsType & { showLine: boolean }> = ({
  typeCode,
  items,
  showLine,
  onCancelLine,
}) => {
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No item lines.
      </Typography>
    );
  }

  if (isMaterialType(typeCode)) {
    // Lines are different profiles and sizes, so a millimetre total across
    // them means nothing to a buyer - the count of parts does (Olena's
    // validation, 05.10.26). The per-line mm stays on each line.
    //
    // Ten columns did not fit the dialog, so related values share a cell:
    // certificate under the material, dimensions under the profile, parts
    // with their length, cutting length with the material it needs. Notes get
    // a full-width row under their line, and only when there is one.
    const totalParts = items.reduce(
      (sum, item) => sum + (item.number_of_parts ?? 0),
      0
    );
    return (
      <TableContainer component={Paper} variant="outlined" sx={NO_SCROLL_CONTAINER_SX}>
        <Table size="small" sx={FIT_TABLE_SX}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "22%" }}>Material</TableCell>
              <TableCell sx={{ width: "20%" }}>Profile</TableCell>
              <TableCell align="right" sx={{ width: "15%" }}>Parts × Length</TableCell>
              <TableCell align="right" sx={{ width: "17%" }}>Cut / Required</TableCell>
              {showLine && <LineHeadCells />}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <Fragment key={item.id}>
                <TableRow sx={rowSx(item, !!item.description)}>
                  <TableCell>
                    {item.material_type_label ?? item.material_type_code ?? "-"}
                    {item.material_type_other_description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.material_type_other_description}
                      </Typography>
                    )}
                    {item.certificate_required !== null && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.certificate_required ? "Certificate required" : "No certificate"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.profile_type_label ?? item.profile_type_code ?? "-"}
                    {dimensionsOf(item) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {dimensionsOf(item)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={NUMBERS_SX}>
                    {fmt(item.number_of_parts)} × {fmt(item.part_length_mm)} mm
                  </TableCell>
                  <TableCell align="right" sx={NUMBERS_SX}>
                    {fmt(item.total_cutting_length_mm)} / {fmt(item.total_required_material_mm)} mm
                  </TableCell>
                  {showLine && <LineCells item={item} onCancelLine={onCancelLine} />}
                </TableRow>
                {item.description && (
                  <TableRow>
                    <TableCell colSpan={showLine ? 6 : 4} sx={{ pt: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        Note: {item.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        <Typography variant="body2" sx={{ p: 1.5, textAlign: "right" }}>
          <strong>Total # Parts (all lines):</strong> {fmt(totalParts)}
        </Typography>
      </TableContainer>
    );
  }

  if (isPartBasedType(typeCode)) {
    // A part plus a quantity - the part carries the rest, so there is no
    // material spec to tabulate.
    const totalQuantity = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
    return (
      <TableContainer component={Paper} variant="outlined" sx={NO_SCROLL_CONTAINER_SX}>
        <Table size="small" sx={FIT_TABLE_SX}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "40%" }}>Part</TableCell>
              <TableCell align="right" sx={{ width: "15%" }}>Quantity</TableCell>
              <TableCell sx={{ width: "45%" }}>Notes</TableCell>
              {showLine && <LineHeadCells />}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} sx={rowSx(item)}>
                <TableCell>{item.product_name ?? "-"}</TableCell>
                <TableCell align="right">{fmt(item.quantity)}</TableCell>
                <TableCell>{item.description || "-"}</TableCell>
                {showLine && <LineCells item={item} onCancelLine={onCancelLine} />}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="body2" sx={{ p: 1.5, textAlign: "right" }}>
          <strong>Total Quantity (all lines):</strong> {totalQuantity.toLocaleString()}
        </Typography>
      </TableContainer>
    );
  }

  if (typeCode === REQ_TYPE_TOOL) {
    const totalQuantity = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
    return (
      <TableContainer component={Paper} variant="outlined" sx={NO_SCROLL_CONTAINER_SX}>
        <Table size="small" sx={FIT_TABLE_SX}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "25%" }}>Tool Name / ID</TableCell>
              <TableCell sx={{ width: "20%" }}>Category</TableCell>
              <TableCell align="right" sx={{ width: "15%" }}>Quantity</TableCell>
              <TableCell sx={{ width: "40%" }}>Description</TableCell>
              {showLine && <LineHeadCells />}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} sx={rowSx(item)}>
                <TableCell>{item.tool_name ?? "-"}</TableCell>
                <TableCell>{item.tool_category_label ?? item.tool_category_code ?? "-"}</TableCell>
                <TableCell align="right">{fmt(item.quantity)}</TableCell>
                <TableCell>{item.description || "-"}</TableCell>
                {showLine && <LineCells item={item} onCancelLine={onCancelLine} />}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="body2" sx={{ p: 1.5, textAlign: "right" }}>
          <strong>Total Quantity (all lines):</strong> {totalQuantity.toLocaleString()}
        </Typography>
      </TableContainer>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      Unknown requisition type.
    </Typography>
  );
};

export default RequisitionDetailItemsTable;
